import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';

import SubtleBadge from 'components/common/SubtleBadge';

import { adminShippingProviderApi } from 'api';

const ShippingProviderReorderModal = ({ show, onHide, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);

  const [isFetching, setIsFetching] = useState(false);
  const [shippingProviders, setShippingProviders] = useState([]);

  useEffect(() => {
    const fetchShippingAddress = async () => {
      if (show) {
        setIsFetching(true);
        try {
          const response = await adminShippingProviderApi.getList();

          setShippingProviders(response);
        } catch (err) {
          toast.error('Failed to fetch shipping provider');
          onHide();
        } finally {
          setIsFetching(false);
        }
      }
    };

    fetchShippingAddress();
  }, [show, onHide]);

  const handleDragEnd = result => {
    if (!result.destination) return;

    const items = Array.from(shippingProviders);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setShippingProviders(items);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const updatedOrder = shippingProviders.map((provider, index) => ({
        id: provider.id,
        sort_order: index,
      }));

      await adminShippingProviderApi.updateShippingProviderOrder(updatedOrder);
      toast.success('Shipping providers reordered successfully');
      onSuccess();
      onHide();
    } catch (err) {
      toast.error('Failed to reorder shipping providers');
    } finally {
      setIsLoading(false);
    }
  };

  const DraggableShippingProviderItem = ({ provider, index }) => (
    <Draggable
      key={provider.id}
      draggableId={provider.id.toString()}
      index={index}
    >
      {provided => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className="p-3 bg-white rounded border d-flex align-items-center mb-3"
        >
          <div>
            <div className="fw-bold">{provider.shipping_provider_type}</div>
            <SubtleBadge bg={provider.is_active ? 'success' : 'danger'}>
              {provider.is_active ? 'Active' : 'InActive'}
            </SubtleBadge>
          </div>
        </div>
      )}
    </Draggable>
  );

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Reorder Shipping Providers</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isFetching ? (
          <div className="d-flex justify-content-center align-items-center p-4">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="shipping-providers">
              {provided => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="p-2"
                >
                  {shippingProviders.map((provider, index) => (
                    <DraggableShippingProviderItem
                      key={provider.id}
                      provider={provider}
                      index={index}
                    />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ShippingProviderReorderModal;
